import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Flag,
  Loader2,
  Play,
  Target,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Card } from "@/platform/ui/card";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import {
  finishSimulation,
  loadSimulationSession,
  recordSimAttempt,
  updateSimAttempt,
  SIM_DURATION_SECONDS,
  startSimulationSession,
  type SimQuestion,
} from "@/tenants/usp/features/simulator-1f/api";

const ALT_LETTERS = ["A", "B", "C", "D", "E"] as const;
const STORAGE_KEY = "fuvest_1f_session_id";

/**
 * `/usp/1afase` — simulador 1ª fase Fuvest.
 *
 * 90 questões objetivas em 5h. Timer regressivo. Navegação livre por grid.
 * Persiste em usp_sessions + usp_attempts. Resume automaticamente se sessão
 * estiver aberta no localStorage.
 */
export function UspSimulador1aFasePage() {
  const { session } = useSession();
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [finished, setFinished] = useState<{
    total: number;
    correct: number;
    byDiscipline: Record<string, { total: number; correct: number }>;
    durationSeconds: number;
  } | null>(null);

  const loadQuery = useQuery({
    queryKey: ["usp-sim-1f", sessionId],
    queryFn: () => loadSimulationSession(sessionId!),
    enabled: !!sessionId && !finished,
    retry: false,
  });

  const startMut = useMutation({
    mutationFn: () => startSimulationSession(session!.user.id),
    onSuccess: (data) => {
      localStorage.setItem(STORAGE_KEY, data.sessionId);
      setSessionId(data.sessionId);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Não foi possível iniciar."),
  });

  const finishMut = useMutation({
    mutationFn: () => finishSimulation(sessionId!),
    onSuccess: (res) => {
      localStorage.removeItem(STORAGE_KEY);
      setFinished(res);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Não foi possível finalizar."),
  });

  // Erro ao carregar sessão antiga, ou sessão já finalizada → limpa.
  // setState precisa estar em effect, nunca no corpo do render.
  useEffect(() => {
    if (loadQuery.error || loadQuery.data?.finishedAt) {
      localStorage.removeItem(STORAGE_KEY);
      setSessionId(null);
    }
  }, [loadQuery.error, loadQuery.data?.finishedAt]);

  if (finished) return <SimSummary result={finished} />;
  if (!sessionId) return <StartScreen onStart={() => startMut.mutate()} loading={startMut.isPending} />;

  if (loadQuery.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-[color:var(--color-muted-foreground)]" />
      </div>
    );
  }
  if (!loadQuery.data || loadQuery.data.finishedAt) return null;

  return (
    <SimRunning
      sessionId={sessionId}
      userId={session!.user.id}
      questions={loadQuery.data.questions}
      startedAt={loadQuery.data.startedAt}
      onFinish={() => finishMut.mutate()}
      finishing={finishMut.isPending}
    />
  );
}

function StartScreen({ onStart, loading }: { onStart: () => void; loading: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-24 w-14 opacity-50 animate-float" />

      <Container size="default" className="relative py-12 md:py-20">
        <Link
          to="/usp/painel"
          className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
        >
          <ArrowLeft className="size-3.5" />
          Painel
        </Link>

        <div className="mt-8 max-w-2xl animate-fade-up">
          <Badge variant="accentSolid" className="gap-2">
            <Target className="size-3" />
            Simulado 1ª fase Fuvest
          </Badge>
          <h1 className="mt-5 font-display text-[length:var(--text-display-lg)] font-semibold leading-tight tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            90 questões. 5 horas. Formato Fuvest.
          </h1>
          <p className="mt-4 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-relaxed">
            Questões reais do corpus Fuvest 2024-2026, balanceadas conforme distribuição histórica
            (Geografia 13, Física 11, Matemática 10, Química 10, História 9...). Cronômetro
            regressivo, navegação livre entre questões.
          </p>
        </div>

        <Card className="mt-10 p-7 md:p-10">
          <ul className="space-y-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            <li className="flex gap-2.5">
              <Clock className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-accent-deep)" }} />
              <span>5 horas — cronômetro inicia ao clicar "Começar".</span>
            </li>
            <li className="flex gap-2.5">
              <Flag className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-accent-deep)" }} />
              <span>Pode pular e voltar. Marca as questões que respondeu no grid lateral.</span>
            </li>
            <li className="flex gap-2.5">
              <TrendingUp className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-accent-deep)" }} />
              <span>Ao finalizar, vê acertos por disciplina + nota projetada Eng. Produção.</span>
            </li>
            <li className="flex gap-2.5">
              <AlertCircle className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-warning)" }} />
              <span>Imagens das questões ainda não estão disponíveis no demo. Questões com gráfico exigem o caderno real.</span>
            </li>
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => navigate("/usp/painel")}>
              Voltar
            </Button>
            <Button size="lg" variant="gradient" onClick={onStart} loading={loading}>
              Começar agora
              <Play className="size-4" />
            </Button>
          </div>
        </Card>
      </Container>
    </div>
  );
}

function SimRunning({
  sessionId,
  userId,
  questions,
  startedAt,
  onFinish,
  finishing,
}: {
  sessionId: string;
  userId: string;
  questions: SimQuestion[];
  startedAt: string;
  onFinish: () => void;
  finishing: boolean;
}) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Map<string, string>>(new Map());
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const startMsRef = useRef<number>(new Date(startedAt).getTime());
  const questionStartRef = useRef<number>(Date.now());
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, SIM_DURATION_SECONDS - Math.floor((Date.now() - startMsRef.current) / 1000)),
  );

  // Timer regressivo
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startMsRef.current) / 1000);
      const left = Math.max(0, SIM_DURATION_SECONDS - elapsed);
      setSecondsLeft(left);
      if (left === 0) {
        clearInterval(interval);
        toast.info("Tempo esgotado — finalizando.");
        onFinish();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [onFinish]);

  // Reseta o "questionStart" ao mudar de questão (mede tempo por questão)
  useEffect(() => {
    questionStartRef.current = Date.now();
  }, [currentIdx]);

  const current = questions[currentIdx];

  async function selectAnswer(letter: string) {
    if (!current) return;
    const previous = answers.get(current.id);
    const next = new Map(answers);
    next.set(current.id, letter);
    setAnswers(next);
    const timeSec = Math.floor((Date.now() - questionStartRef.current) / 1000);
    try {
      if (previous) {
        await updateSimAttempt({
          sessionId,
          questionId: current.id,
          selected: letter,
          correctAnswer: current.correct_answer,
          timeSeconds: timeSec,
        });
      } else {
        await recordSimAttempt({
          userId,
          sessionId,
          questionId: current.id,
          selected: letter,
          correctAnswer: current.correct_answer,
          timeSeconds: timeSec,
        });
      }
    } catch (err) {
      console.error("[sim] record attempt failed", err);
    }
  }

  const answeredCount = answers.size;

  if (!current) return null;

  return (
    <div className="min-h-[calc(100vh-56px)]">
      {/* Top bar com timer */}
      <div className="sticky top-14 z-30 border-b border-[--color-border] bg-[color:var(--color-background)]/95 backdrop-blur">
        <Container size="wide" className="py-3">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <Badge variant="accentSolid" className="gap-1.5 shrink-0">
                <Target className="size-3" />
                {currentIdx + 1}/{questions.length}
              </Badge>
              <span className="hidden sm:inline text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                {answeredCount} respondidas
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <CountdownTimer secondsLeft={secondsLeft} />
              <Button
                size="sm"
                variant={secondsLeft < 300 || answeredCount >= questions.length * 0.9 ? "primary" : "outline"}
                onClick={() => setShowFinishConfirm(true)}
                loading={finishing}
              >
                <span className="hidden sm:inline">Finalizar</span>
                <span className="sm:hidden">Fim</span>
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container size="wide" className="py-6 md:py-12">
        <div className="flex flex-col gap-6 lg:grid lg:gap-8 lg:grid-cols-[1fr_220px]">
          {/* Question */}
          <article className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 md:p-9 shadow-[var(--shadow-sm)]">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {current.discipline}
              </Badge>
              <span className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Fuvest {current.year} · Q{current.number}
              </span>
            </div>

            {current.has_base_text && current.base_text && (
              <blockquote className="mt-5 rounded-xl border-l-4 border-[--color-border] bg-[color:var(--color-muted)]/30 p-4 text-[length:var(--text-body-sm)] italic text-[color:var(--color-muted-foreground)] leading-relaxed">
                {current.base_text}
              </blockquote>
            )}

            <p className="mt-5 whitespace-pre-line text-[length:var(--text-body)] leading-relaxed">
              {current.stem}
            </p>

            {(current.has_image || current.has_graph || current.has_table) && current.pageImageUrl && (
              <details className="mt-4 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/20 p-3">
                <summary className="cursor-pointer text-[length:var(--text-body-sm)] font-medium text-[color:var(--color-accent)]">
                  Ver página original (gráfico/imagem/tabela)
                </summary>
                <img
                  src={current.pageImageUrl}
                  alt={`Fuvest ${current.year} — página da questão ${current.number}`}
                  loading="lazy"
                  className="mt-3 w-full rounded-lg border border-[--color-border]"
                />
              </details>
            )}

            <ul className="mt-7 space-y-2.5" role="radiogroup">
              {ALT_LETTERS.map((letter) => {
                const text = current.alternatives?.[letter];
                if (!text) return null;
                const isSelected = answers.get(current.id) === letter;
                return (
                  <li key={letter}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => selectAnswer(letter)}
                      className={cn(
                        "w-full text-left rounded-xl border-2 p-4 transition-colors duration-150 cursor-pointer",
                        isSelected
                          ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]"
                          : "border-[--color-border] hover:border-[--color-accent]/40",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={cn(
                            "mt-0.5 grid size-6 shrink-0 place-items-center rounded-full font-mono text-[length:var(--text-caption)] font-bold",
                            isSelected
                              ? "bg-[color:var(--color-accent)] text-white"
                              : "bg-[color:var(--color-muted)]",
                          )}
                        >
                          {letter}
                        </span>
                        <span className="flex-1 text-[length:var(--text-body-sm)] leading-relaxed">
                          {text}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-8 flex justify-between gap-3">
              <Button
                variant="outline"
                disabled={currentIdx === 0}
                onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
              >
                <ArrowLeft className="size-4" />
                Anterior
              </Button>
              <Button
                variant="primary"
                disabled={currentIdx === questions.length - 1}
                onClick={() => setCurrentIdx((i) => Math.min(questions.length - 1, i + 1))}
              >
                Próxima
                <ArrowUpRight className="size-4" />
              </Button>
            </div>
          </article>

          {/* Grid de navegação */}
          <aside className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-4 lg:sticky lg:top-32 lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto h-fit">
            <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)] mb-3">
              Navegação
            </p>
            <div className="grid grid-cols-6 gap-2 sm:grid-cols-8 lg:grid-cols-5 lg:gap-1.5">
              {questions.map((q, idx) => {
                const isAnswered = answers.has(q.id);
                const isCurrent = idx === currentIdx;
                return (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => setCurrentIdx(idx)}
                    className={cn(
                      "aspect-square rounded-md text-[length:var(--text-caption)] font-mono font-semibold transition-colors",
                      isCurrent && "ring-2 ring-[color:var(--color-accent)] ring-offset-1",
                      isAnswered && !isCurrent && "bg-[color:var(--color-accent)] text-white",
                      !isAnswered && !isCurrent && "bg-[color:var(--color-muted)] text-[color:var(--color-muted-foreground)] hover:bg-[color:var(--color-muted)]/80",
                      isCurrent && isAnswered && "bg-[color:var(--color-accent)] text-white",
                      isCurrent && !isAnswered && "bg-[color:var(--color-card)]",
                    )}
                    aria-label={`Questão ${idx + 1}${isAnswered ? " (respondida)" : ""}`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
              <span className="inline-block size-2 rounded-sm bg-[color:var(--color-accent)] align-middle" />{" "}
              respondida
            </p>
          </aside>
        </div>
      </Container>

      {showFinishConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto"
          onClick={() => setShowFinishConfirm(false)}
        >
          <Card
            className="w-full max-w-md max-h-[90vh] overflow-y-auto p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">Finalizar simulado?</h3>
            <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              Você respondeu {answeredCount} de {questions.length} questões. Não dá pra voltar depois
              de finalizar.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setShowFinishConfirm(false)} className="w-full sm:w-auto">
                Continuar respondendo
              </Button>
              <Button variant="primary" onClick={onFinish} loading={finishing} className="w-full sm:w-auto">
                Finalizar
                <CheckCircle2 className="size-4" />
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function CountdownTimer({ secondsLeft }: { secondsLeft: number }) {
  const h = Math.floor(secondsLeft / 3600);
  const m = Math.floor((secondsLeft % 3600) / 60);
  const s = secondsLeft % 60;
  const isCritical = secondsLeft < 600; // <10min

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[length:var(--text-body-sm)] font-medium tabular-nums",
        isCritical
          ? "bg-[color:var(--color-danger)] text-white animate-pulse"
          : "bg-[color:var(--color-muted)] text-[color:var(--color-foreground)]",
      )}
    >
      <Clock className="size-3.5" />
      {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </div>
  );
}

function SimSummary({
  result,
}: {
  result: {
    total: number;
    correct: number;
    durationSeconds: number;
    byDiscipline: Record<string, { total: number; correct: number }>;
  };
}) {
  const pct = Math.round((result.correct / result.total) * 100);
  const h = Math.floor(result.durationSeconds / 3600);
  const m = Math.floor((result.durationSeconds % 3600) / 60);

  const byDisc = useMemo(
    () =>
      Object.entries(result.byDiscipline)
        .map(([d, s]) => ({ discipline: d, ...s, pct: Math.round((s.correct / s.total) * 100) }))
        .sort((a, b) => a.pct - b.pct),
    [result],
  );

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-24 w-14 opacity-60 animate-float" />

      <Container size="default" className="relative py-16 md:py-24">
        <div className="text-center animate-fade-up">
          <Badge
            className="gap-1.5"
            style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
          >
            <CheckCircle2 className="size-3" />
            Simulado finalizado
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-tight tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            Você acertou <span className="text-gradient-accent">{result.correct} de {result.total}</span>.
          </h1>
          <p className="mt-3 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)]">
            {pct}% em {h}h{String(m).padStart(2, "0")}.
          </p>
        </div>

        <section className="mt-12">
          <h2 className="font-display text-[length:var(--text-display-sm)] font-semibold mb-5">
            Por disciplina
          </h2>
          <ul className="space-y-2">
            {byDisc.map((d) => (
              <li
                key={d.discipline}
                className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-4"
              >
                <div className="flex items-center gap-3">
                  <span className="w-32 text-[length:var(--text-body-sm)] font-medium capitalize">
                    {d.discipline}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                    <div
                      className="h-full"
                      style={{
                        width: `${d.pct}%`,
                        backgroundColor:
                          d.pct >= 70
                            ? "var(--color-success)"
                            : d.pct >= 40
                              ? "var(--color-warning)"
                              : "var(--color-danger)",
                      }}
                    />
                  </div>
                </div>
                <span
                  data-tabular="true"
                  className="text-[length:var(--text-body-sm)] font-medium tabular-nums"
                >
                  {d.correct}/{d.total}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-10 flex justify-center gap-3">
          <Button asChild size="lg" variant="primary">
            <Link to="/usp/painel">
              <ArrowLeft className="size-4" />
              Voltar para o painel
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/usp/1afase">
              <Target className="size-4" />
              Fazer outro simulado
            </Link>
          </Button>
        </div>
      </Container>
    </div>
  );
}

